import { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useBreadcrumbContext } from '@/components/Breadcrumb/context';
import { BREADCRUMB_QUERY_PARAM } from '@/components/Breadcrumb/hooks/useBreadcrumb';
import useNavigation from '@/hooks/useNavigation';
import { PREVIEW_STATUS_KEY } from '@/hooks/usePreviewHeader/consts';
import useSearchParams from '@/hooks/useSearchParam';
import getFields from '@/modules/corporate/modules/database/utils/getFields';
import { getNavigationQuery } from '@/modules/corporate/utils/getNavigationQuery';
import routes from '@/pages/corporate/routes';
import {
  DatabaseMetadataType,
  ResourceStatusEnum,
  ResourceTypeEnum,
  ViewTypeEnum,
} from '@/types/schema';

import { SurveyDetailsContentFragment } from '../../graphql/fragments/generated/SurveyDetailsContent';
import { useSurveyDetailsQuery } from '../../graphql/queries/generated/surveyDetails';

interface Params {
  contentId: string;
  isPreviewing?: boolean;
  isEmbed?: boolean;
  parentContentId?: string;
}

const useSurveyDetails = ({ contentId, isPreviewing, isEmbed, parentContentId }: Params) => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const versionStatus = useSearchParams(PREVIEW_STATUS_KEY) as ResourceStatusEnum;
  const { breadcrumbUrl } = useBreadcrumbContext();
  const [{ fetching, error, data }] = useSurveyDetailsQuery({
    variables: {
      id: contentId,
      parentContentId,
      versionStatus:
        isPreviewing &&
        [ResourceStatusEnum.Draft, ResourceStatusEnum.Published].includes(versionStatus)
          ? versionStatus
          : undefined,
      isEmbed,
    },
  });
  const survey = data?.content as SurveyDetailsContentFragment | undefined;
  const databaseMetadata = survey?.databaseMetadata as DatabaseMetadataType | undefined;
  const databaseView = survey?.databaseViews?.find(
    (view) => view.viewType === ViewTypeEnum.FormView,
  );

  const fields = useMemo(() => {
    const metadataFields = databaseMetadata?.fields;
    const viewFields = databaseView?.format.fields;

    if (metadataFields && viewFields) {
      return getFields({
        viewFields,
        metadataFields,
      });
    }
    return [];
  }, [databaseMetadata?.fields, databaseView?.format.fields]);

  const onBack = useCallback(() => {
    const parentContentProfile = survey?.parentContentProfile;

    if (!parentContentProfile) {
      navigation.push(routes.Home());
      return;
    }

    switch (parentContentProfile?.type) {
      case ResourceTypeEnum.Path: {
        const query = getNavigationQuery(breadcrumbUrl);
        navigation.push(routes.PathDetails(parentContentProfile.id), { query });
        break;
      }

      case ResourceTypeEnum.Course: {
        navigation.push(routes.CourseDetails(parentContentProfile.id), {
          query: {
            [BREADCRUMB_QUERY_PARAM]: [
              // slice because remove courseid from breadcrumb
              ...(breadcrumbUrl?.length ? breadcrumbUrl.slice(0, -1) : []),
            ],
          },
        });
        break;
      }

      case ResourceTypeEnum.LiveEventContent:
      case ResourceTypeEnum.OfflineEventContent:
      case ResourceTypeEnum.ExternalEventContent: {
        navigation.push(routes.EventDetails(parentContentProfile.id), {
          query: {
            [BREADCRUMB_QUERY_PARAM]: [
              // slice because remove courseid from breadcrumb
              ...(breadcrumbUrl?.length ? breadcrumbUrl.slice(0, -1) : []),
            ],
          },
        });
        break;
      }

      default:
        navigation.push(routes.Home());
    }
  }, [breadcrumbUrl, navigation, survey?.parentContentProfile]);

  const backActionText = useMemo(() => {
    switch (survey?.parentContentProfile?.type) {
      case ResourceTypeEnum.Path: {
        return t('Common:Actions.BackToPath');
      }

      case ResourceTypeEnum.Course: {
        return t('Common:Actions.BackToCourse');
      }

      case ResourceTypeEnum.LiveEventContent:
      case ResourceTypeEnum.OfflineEventContent:
      case ResourceTypeEnum.ExternalEventContent: {
        return t('Common:Actions.BackToSession');
      }
      default:
        return t('Common:Actions.BackToHome');
    }
  }, [survey?.parentContentProfile?.type, t]);

  return {
    fetching: fetching,
    error: error,
    survey,
    fields,
    onBack,
    backActionText,
  };
};

export default useSurveyDetails;
